import { Box, Stack, Typography } from '@mui/material'
import { ICellRendererParams } from 'ag-grid-community'
import { getOrgTeamsTable, getTeamMembers, getTeamsTable, removeTeam } from 'api/teams'
import ConfirmRemoveModal from 'components/modals/ConfirmRemoveModal'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import TableToolbar from 'components/table/TableToolbar'
import { useCohortsFilter } from 'features/Cohorts'
import { DateRangeToolbar } from 'features/Reports/components/DateRangeToolbar/DateRangeToolbar'
import { prepareDateRangeParams } from 'features/Reports/helpers/prepareDateRangeParams'
import { useDateRange } from 'features/Reports/hooks/useDateRange'
import { getActionColumn, RowAction, useOrgStateUnitFilter } from 'features/UI'
import { useExportCSV } from 'hooks/useExportCSV'
import { useGridControl } from 'hooks/useGridControl'
import { useRemoveModalControl } from 'hooks/useRemoveModalControl'
import React, { ReactNode, useCallback, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { BaseTableRequest, FilterModel, TeamInfo, TeamMember, TeamType } from 'types'
import { useGetDataSource } from 'utils'
import { TeamAddControl } from '../TeamAddControl/TeamAddControl'
import { TeamsPageMemberControlAdd } from './TeamsPageMemberControlAdd'
import { useTeamsPageColumns } from './useTeamsPageColumns'

export function TeamsPage({
  isOrg = false,
  toolbarLeft,
}: {
  isOrg?: boolean
  toolbarLeft?: ReactNode
}) {
  const { cohortsFilterValue, CohortsFilter } = useCohortsFilter()
  const { orgStateUnitFilterValue, OrgStateUnitFilter } = useOrgStateUnitFilter()
  const [teamNameToRemove, setTeamNameToRemove] = useState('')
  const [activeTeamState, setActiveTeamState] = useState<{
    teamId: number | null
    teamName: string | null
    teamType: TeamType | null
    organizationId: number | null
    teamMembers: TeamMember[]
  }>({
    teamId: null,
    teamName: null,
    teamType: null,
    organizationId: null,
    teamMembers: [],
  })

  const {
    rangeType,
    year,
    month,
    selectedDate,
    dateInterval,
    onChangeDateInterval,
    onChangeRange,
    onChangeYear,
    onChangeMonth,
    setSelectedDate,
  } = useDateRange()

  const teamMembersQuery = useQuery(
    ['getTeamMembers', activeTeamState.teamId],
    () => {
      if (!activeTeamState.teamId) return null

      return getTeamMembers({
        id: activeTeamState.teamId,
      })
    },
    {
      enabled: activeTeamState.teamId !== null,
      onSuccess: (data) => {
        if (data) {
          setActiveTeamState((prev) => ({
            ...prev,
            teamMembers: data.data.rows,
          }))
        }
      },
      onError: () => {
        setActiveTeamState((prev) => ({
          ...prev,
          teamMembers: [],
        }))
      },
    },
  )

  const getApiWrapper = useCallback(
    (req: BaseTableRequest) => {
      const dateParams = prepareDateRangeParams(rangeType, year, month, selectedDate, dateInterval)

      const filter: FilterModel = {
        ...(req.filter || {}),
      }

      if (cohortsFilterValue) {
        filter.cohortId = cohortsFilterValue
      }

      if (orgStateUnitFilterValue) {
        filter.accessCodeId = orgStateUnitFilterValue
      }

      const apiMethod = isOrg ? getOrgTeamsTable : getTeamsTable

      return apiMethod({
        ...req,
        filter,
        params: dateParams,
      })
    },
    [
      cohortsFilterValue,
      dateInterval,
      month,
      rangeType,
      selectedDate,
      year,
      isOrg,
      orgStateUnitFilterValue,
    ],
  )

  const getDataSource = useGetDataSource(getApiWrapper)
  const { onGridReady, gridApi, columnApi } = useGridControl(getDataSource)

  const handleAddMember = useCallback((teamId: number, props: ICellRendererParams<TeamInfo>) => {
    setActiveTeamState({
      teamId,
      teamName: props.data?.name || null,
      teamType: props.data?.type || null,
      teamMembers: [],
      organizationId: props.data?.organizationId || null,
    })
  }, [])

  const handleRefetchAfterAddMember = useCallback(async () => {
    // 1. Refetch team members
    await teamMembersQuery.refetch()

    // 2. Refresh grid data
    if (gridApi) {
      gridApi.refreshInfiniteCache()
    }

    // 3. Reset state
    setActiveTeamState({
      teamId: null,
      teamName: null,
      teamType: null,
      teamMembers: [],
      organizationId: null,
    })
  }, [teamMembersQuery, gridApi])

  const handleCloseModal = useCallback(() => {
    setActiveTeamState({
      teamId: null,
      teamName: null,
      teamType: null,
      teamMembers: [],
      organizationId: null,
    })
  }, [])

  const handleRefetchGrid = useCallback(async () => {
    await teamMembersQuery.refetch()
    if (gridApi) {
      gridApi.refreshInfiniteCache()
    }
  }, [gridApi, teamMembersQuery])

  const { idToRemove, openDeleteModal, closeDeleteModal, removeLoading, handleConfirmRemove } =
    useRemoveModalControl({
      deleteFunc: removeTeam,
      warning: "Can't delete team",
      successCallback: () => {
        gridApi?.refreshInfiniteCache()
      },
    })

  const handleRemove = useCallback(
    (id: number, props: ICellRendererParams<TeamInfo>) => {
      setTeamNameToRemove(props.data?.name || '')
      openDeleteModal(id)
    },
    [openDeleteModal],
  )

  const actions: RowAction[] = useMemo(() => {
    return [
      {
        name: 'Add Member',
        onClick: handleAddMember,
      },
      {
        name: 'Remove',
        onClick: handleRemove,
      },
    ]
  }, [handleAddMember, handleRemove])

  const { csvFields, columnDefs } = useTeamsPageColumns(isOrg, handleRefetchGrid)

  const columns = useMemo(() => {
    return columnDefs.concat(getActionColumn(actions))
  }, [actions, columnDefs])

  const { csvLoading, onExportCSV } = useExportCSV(
    getApiWrapper,
    gridApi,
    columnApi,
    csvFields,
    'Teams.csv',
  )

  return (
    <>
      <TableToolbar
        title=""
        left={toolbarLeft}
        exportCSVEnable
        csvLoading={csvLoading}
        onExportCSV={onExportCSV}
      >
        <TeamAddControl isOrg={isOrg} onRefetch={handleRefetchGrid} />
      </TableToolbar>

      {isOrg && <Typography mb={1}>View, Add and Modify Your Teams</Typography>}

      <Stack flexDirection="row" mt={1} mb={2} className="gap-16">
        <DateRangeToolbar
          rangeType={rangeType}
          year={year}
          month={month}
          dateInterval={dateInterval}
          onChangeDateInterval={onChangeDateInterval}
          selectedDate={selectedDate}
          onChangeRange={onChangeRange}
          onChangeYear={onChangeYear}
          onChangeMonth={onChangeMonth}
          setSelectedDate={setSelectedDate}
        />

        {!isOrg && <Box minWidth="220px">{CohortsFilter}</Box>}
        {isOrg && OrgStateUnitFilter}
      </Stack>

      <Box mb={2} height="100%">
        <InfinityAgGrid pagination columnDefs={columns} onGridReady={onGridReady} />
      </Box>

      {/* Add Member Modal */}
      {activeTeamState.teamId && (
        <TeamsPageMemberControlAdd
          teamId={activeTeamState.teamId}
          teamName={activeTeamState.teamName}
          teamType={activeTeamState.teamType}
          organizationId={activeTeamState.organizationId}
          teamMembersData={activeTeamState.teamMembers}
          onRefetchTeamMembers={handleRefetchAfterAddMember}
          onClose={handleCloseModal}
        />
      )}

      <ConfirmRemoveModal
        entityToRemove={`Team "${teamNameToRemove}"`}
        loading={removeLoading}
        isOpen={idToRemove !== null}
        handleConfirm={handleConfirmRemove}
        handleClose={closeDeleteModal}
      />
    </>
  )
}
